/* @flow */
/* eslint-env es6,browser */

import adrotation from './adrotation';
import { _adContainer } from './adcontainer';
import { BrowserUtil } from './utils/browserutil';

const ONE_STR = '1',
  TWO_STR = '2',
  FRAME_STR = 'F';
export const FRAME_PATTERN = new RegExp(`${FRAME_STR}(${ONE_STR}|${TWO_STR})`);

/**
 * @description Implements cached adcontainer functionality
 * @exports cachedAdcontainer
 * @requires module:adcontainer
 * @requires module:adrotation
 * @requires module:browserutil
 */
class _cachedAdContainer extends _adContainer {
  /**
   * Update ad container information for given position
   * @param {Object} adInfoObj - Ad info object to be updated
   * @param {boolean} bActive - Should get details about active/inactive?
   * @returns {undefined}
   * @private
   **/
  _updateAdContainerInfo(adInfoObj: adInfo, bActive: boolean = false): void {
    let adFrame = this._getActiveInactiveFrame(adInfoObj.id),
      // if active container is empty use it even if bActive is false
      // this is to take care of showing up ad in first load w/o toggle
      adDivObj = this._getAdDiv(adFrame.activeContainer);
    bActive = adDivObj && !adDivObj.firstChild ? true : bActive;
    adInfoObj.fID = bActive ? adFrame.activeFrame : adFrame.inactiveFrame;
    adInfoObj.cID = bActive
      ? adFrame.activeContainer
      : adFrame.inactiveContainer;
  }

  /**
   * Utility functions for adding caching divs
   * @param {string} adPos - ad position
   * @returns {undefined}
   * @private
   */
  _addCachingDivs(adPos: string): void {
    if (this._getAdDiv(adPos + ONE_STR) && this._getAdDiv(adPos + TWO_STR)) {
      return; //already created
    }
    let adDivObj = this._getAdDiv(adPos + this._sWrapper);
    if (adDivObj) {
      adDivObj.innerHTML = `<div id='${adPos}${ONE_STR}' style='display: block;top: 0px;left: 0px;background-color: transparent;'></div>
      <div id='${adPos}${TWO_STR}' style='display: none;top: 0px;left: 0px;background-color: transparent;'></div>`;
    }
  }

  /**
   * getter for active/inactive adframe details
   * @param {string} adPos - Ad Position name
   * @returns {Object} - IDs of active & inactive ad iframe and it's containers
   * @private
   */
  _getActiveInactiveFrame(adPos: string): activeInactiveInfo {
    let adDivObj = this._getAdDiv(`${adPos}${ONE_STR}`);
    let [activeID, inactiveID] =
      adDivObj && adDivObj.style.display !== 'none'
        ? [ONE_STR, TWO_STR]
        : [TWO_STR, ONE_STR];
    return {
      activeContainer: `${adPos}${activeID}`,
      activeFrame: `${adPos}${FRAME_STR}${activeID}`,
      inactiveContainer: `${adPos}${inactiveID}`,
      inactiveFrame: `${adPos}${FRAME_STR}${inactiveID}`
    };
  }

  /**
   * Toggle active & inactive containers
   * @param {string} activeCID - Active ad container ID
   * @param {string} inactiveCID - Inactive ad container ID
   * @returns {undefined}
   * @private
   */
  _toggleAd(activeCID: string, inactiveCID: string) {
    try {
      let active = this._getAdDiv(activeCID),
        inactive = this._getAdDiv(inactiveCID);
      if (inactive) {
        inactive.style.display = 'block';
      }
      if (active) {
        active.style.display = 'none';
      }
    } catch (e) {
      /*continue regardless of error*/
    }
  }

  /**
   * Caching is enabled only for adrotation position, iframe ad and non old IE
   * @param {string} adPos - ad position
   * @param {boolean} isIframeAd - is loaded inside IFrame?
   * @returns {boolean} - Caching status
   */
  isCachingEnabled(adPos: string, isIframeAd: boolean | void) {
    if (
      adrotation.getPositions().indexOf(adPos) !== -1 &&
      isIframeAd !== false &&
      !BrowserUtil.isOldIE()
    ) {
      return true;
    }
    return false;
  }

  /**
   * loader iframe AD
   * @param {string} adUrl - static template url for loading ad iframe
   * @param {adInfoObj} adInfoObj - ad Information
   * @returns {DOMIFrameElement} - Newly created ad iframe
   */
  loadIframeAd(adUrl: string, adInfoObj: adInfo) {
    this._addCachingDivs(adInfoObj.id);
    return super.loadIframeAd(adUrl, adInfoObj);
  }

  /**
   * toggle ad during ad rotation
   * @param {string} adPos - Ad position to toggle
   * @returns {undefined}
   */
  toggleAd(adPos: string) {
    let adFrame = this._getActiveInactiveFrame(adPos),
      inactiveDivObj = this._getAdDiv(adFrame.inactiveContainer),
      activeDivObj = this._getAdDiv(adFrame.activeContainer);

    // toggle only if inactive container has content
    // this is avoid toggle during first page load
    if (inactiveDivObj && inactiveDivObj.firstChild) {
      this._toggleAd(adFrame.activeContainer, adFrame.inactiveContainer);
      if (activeDivObj && activeDivObj.ownerDocument) {
        this._cleanAdFrame(
          activeDivObj.ownerDocument,
          activeDivObj,
          adFrame.activeFrame
        );
      }
    }
  }
}

//export singleton object as default for this module
let cachedAdcontainer = new _cachedAdContainer();
export { cachedAdcontainer };
